import { useEffect, useState } from 'react';
import { MUHASEBE_AD, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Detay } from './ortak';

/**
 * MUHASEBE PROGRAMI BAĞI — ürünün muhasebe programındaki karşılığı.
 * Mevcut uçlar: ara (`/api/parasut/urun-ara`), bağla, bağı kaldır, programda oluştur.
 * Bağ yoksa satış faturası bu ürünün stoğunu düşemez (eşleme kuyruğunda bekler).
 */
type Sonuc = { parasutId: string; ad: string; kod: string | null; birim: string | null; bagliKod: string | null };

export default function MuhasebeBagi({ d, yenile, bildir }: {
  d: Detay; yenile: () => Promise<unknown>; bildir: { hata: (m: string | null) => void; bilgi: (m: string | null) => void };
}) {
  const { can, me } = useAuth();
  const baglar = d.aliases.filter((a) => a.supplierCode.startsWith('parasut:'));
  const baglayabilir = can('purchase.match');
  const olusturabilir = can('settings.write');
  const kapali = me?.parasutKip === 'off';
  const kapaliNeden = `${MUHASEBE_AD} bağlantısı henüz kurulmadı`;

  const [araAcik, setAraAcik] = useState(false);
  const [q, setQ] = useState(d.name);
  const [sonuc, setSonuc] = useState<Sonuc[] | null>(null);
  const [yakin, setYakin] = useState(false);
  const [araniyor, setAraniyor] = useState(false);
  const [mesgul, setMesgul] = useState(false);

  useEffect(() => {
    if (!araAcik || q.trim().length < 2) { setSonuc(null); return; }
    let iptal = false;
    setAraniyor(true);
    const t = setTimeout(() => {
      api<{ urunler: Sonuc[]; yakin?: boolean }>(`/api/parasut/urun-ara?q=${encodeURIComponent(q.trim())}`)
        .then((r) => { if (!iptal) { setSonuc(r.urunler); setYakin(!!r.yakin); } })
        .catch((e) => { if (!iptal) { setSonuc([]); bildir.hata(e instanceof Error ? e.message : 'Arama yapılamadı'); } })
        .finally(() => { if (!iptal) setAraniyor(false); });
    }, 350);
    return () => { iptal = true; clearTimeout(t); };
  }, [araAcik, q]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function calistir(is: () => Promise<unknown>, tamam: string, olmadi: string) {
    setMesgul(true); bildir.hata(null); bildir.bilgi(null);
    try { await is(); bildir.bilgi(tamam); setAraAcik(false); await yenile(); }
    catch (e) { bildir.hata(e instanceof Error ? e.message : olmadi); }
    finally { setMesgul(false); }
  }
  const kaldir = () => {
    if (!window.confirm(`${MUHASEBE_AD} bağı kaldırılsın mı? Ürün silinmez; yeni faturalar yeniden bağlanana dek eşleme kuyruğunda bekler.`)) return;
    void calistir(() => api('/api/parasut/urun-bag-kaldir', { method: 'POST', body: JSON.stringify({ productId: d.id }) }), 'Bağ kaldırıldı', 'Bağ kaldırılamadı');
  };
  const bagla = (s: Sonuc) => void calistir(async () => {
    // "Değiştir": önce eski bağ çözülür, sonra yenisi kurulur.
    if (baglar.length > 0) await api('/api/parasut/urun-bag-kaldir', { method: 'POST', body: JSON.stringify({ productId: d.id }) });
    await api('/api/parasut/urun-bagla', { method: 'POST', body: JSON.stringify({ productId: d.id, parasutUrunId: s.parasutId, ad: s.ad }) });
  }, `${MUHASEBE_AD} ürününe bağlandı`, 'Bağlanamadı');
  const olustur = () => {
    if (!window.confirm(`"${d.name}" ${MUHASEBE_AD}'te yeni ürün kartı olarak açılsın mı?`)) return;
    void calistir(() => api('/api/parasut/urun-olustur', { method: 'POST', body: JSON.stringify({ productId: d.id }) }), `${MUHASEBE_AD}'te açıldı ve bağlandı`, `${MUHASEBE_AD} ürünü açılamadı`);
  };

  return (
    <section className="ccd-uk-yan-bolum" id="uk-muhasebe">
      <h3 className="ccd-uk-yan-baslik">Muhasebe programı</h3>
      {baglar.length === 0 ? (
        <p className="ccd-uk-yan-metin">Bu ürün {MUHASEBE_AD}'te bir ürüne bağlı değil. Bağlanınca satış faturası stoğu kendiliğinden düşer.</p>
      ) : (
        baglar.map((a) => (
          <div className="ccd-uk-bag" key={a.id}>
            <span className="ccd-badge ccd-badge-info">{MUHASEBE_AD}</span>
            <span className="ccd-uk-bag-ad">{a.nameSnapshot ?? a.supplierCode.slice('parasut:'.length)}</span>
          </div>
        ))
      )}
      {(baglayabilir || olusturabilir) && !araAcik && (
        <div className="ccd-uk-yan-dugmeler">
          {baglayabilir && (
            <button type="button" className="ccd-btn ccd-btn-sm ccd-sp-hap" disabled={mesgul || kapali} title={kapali ? kapaliNeden : undefined}
                    onClick={() => { setQ(d.name); setAraAcik(true); }}>
              {baglar.length ? 'Değiştir' : 'Bağla'}
            </button>
          )}
          {baglayabilir && baglar.length > 0 && (
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm ccd-sp-hap" disabled={mesgul} onClick={kaldir}>Kaldır</button>
          )}
          {olusturabilir && baglar.length === 0 && (
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm ccd-sp-hap" disabled={mesgul || kapali}
                    title={kapali ? kapaliNeden : `Bu ürünü ${MUHASEBE_AD}'te yeni ürün kartı olarak açar ve bağlar`} onClick={olustur}>
              {MUHASEBE_AD}'te oluştur
            </button>
          )}
        </div>
      )}
      {araAcik && (
        <div className="ccd-uk-bag-ara">
          <input className="ccd-input" autoFocus value={q} placeholder={`${MUHASEBE_AD}'te ara: ad ya da kod…`} aria-label={`${MUHASEBE_AD}'te ürün ara`}
                 onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') setAraAcik(false); }} />
          <div className="ccd-uk-bag-sonuc">
            {araniyor && <div className="ccd-meta">Aranıyor…</div>}
            {!araniyor && sonuc && sonuc.length === 0 && <div className="ccd-meta">Eşleşen ürün yok. Adın bir parçasını deneyin.</div>}
            {!araniyor && yakin && <div className="ccd-meta">Birebir eşleşme yok; yakın sonuçlar:</div>}
            {!araniyor && sonuc?.map((s) => (
              <button type="button" key={s.parasutId} className="ccd-uk-bag-satir" disabled={mesgul || (!!s.bagliKod && s.bagliKod !== d.code)}
                      title={s.bagliKod && s.bagliKod !== d.code ? `Zaten ${s.bagliKod} koduna bağlı` : 'Bu ürüne bağla'} onClick={() => bagla(s)}>
                <span>{s.ad}</span>
                <span className="ccd-meta">{[s.kod, s.birim, s.bagliKod ? `bağlı: ${s.bagliKod}` : null].filter(Boolean).join(' · ') || '—'}</span>
              </button>
            ))}
          </div>
          <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setAraAcik(false)}>Vazgeç</button>
        </div>
      )}
    </section>
  );
}
